import { camelCase, mapKeys } from 'lodash';
import { useQuery, useQueryClient } from 'react-query';

import { ProjectTemplate } from '@experiment-management-shared/types';
import api, { QueryConfig } from '@shared/api';
import useProject from './useProject';
import { useCallback } from 'react';

const VIEW_VERSION = 3;

const getProjectViewDetails = async ({
  projectId,
  templateId,
  signal
}: {
  projectId: string;
  templateId?: string | null;
  signal?: AbortSignal;
}) => {
  const { data: serverView } = await api.get<ProjectTemplate>(
    'dashboard-templates',
    {
      params: { projectId, templateId, version: VIEW_VERSION },
      signal
    }
  );

  return mapKeys(serverView, (_, key) => camelCase(key)) as ProjectTemplate;
};

export default function useProjectViewDetails<SelectData = ProjectTemplate>(
  {
    templateId,
    viewProjectId
  }: {
    templateId: string | null | undefined;
    viewProjectId?: string | null | undefined;
  },
  config: QueryConfig<ProjectTemplate, SelectData> = {}
) {
  const { data: project } = useProject();
  const projectId = viewProjectId || project?.projectId;
  const configEnabled = config?.enabled ?? true;
  const queryClient = useQueryClient();

  const query = useQuery(
    ['projectViewDetails', { projectId, templateId }],
    ({ signal }) => {
      return getProjectViewDetails({ projectId, templateId, signal });
    },
    {
      ...config,
      enabled: configEnabled && !!projectId
    }
  );

  const refetchWithNewParams = useCallback(
    async ({ templateId: newTemplateId }) => {
      if (!projectId) return { data: null };

      const data = await getProjectViewDetails({
        projectId,
        templateId: newTemplateId
      });

      queryClient.setQueryData(
        ['projectViewDetails', { projectId, templateId: newTemplateId }],
        data
      );

      return { data };
    },
    [projectId, queryClient]
  );

  return {
    ...query,
    refetchWithParams: refetchWithNewParams
  };
}
